import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

/**
 * Onboarding portal (implementation portal) API.
 *
 * Deliberately NOT using `baseQueryWithAuth`: this page is reached by customers who
 * have no Nudge login at all, and its 401 means "confirm your email", not "your
 * session died". Routing it through the shared base query would hand the 401 to
 * handleUnauthorized and bounce the customer to /login, which is exactly the dead
 * end this feature exists to remove.
 */

export type StepOwner = "customer" | "nudge";

export type StepStatus = "locked" | "awaiting_customer" | "in_progress_nudge" | "blocked" | "complete";

export type StepKey = "baa" | "group_admin" | "ehr_login" | "templates" | "ai_assistant" | "go_live";

export type SetupStep = {
  key: StepKey;
  title: string;
  order: number;
  owner: StepOwner;
  owner_label: string;
  status: StepStatus;
  status_label: string;
  status_detail: string;
  typical_duration_label: string;
  started_at: string | null;
  completed_at: string | null;
};

export type SetupBanner = {
  kind: "action_needed" | "nudge_working" | "complete";
  headline: string;
  body: string;
  step_key: StepKey | null;
};

export type SetupPortal = {
  group_name: string;
  viewer_email: string;
  steps: SetupStep[];
  progress: {
    completed: number;
    total: number;
    current_position: number;
    current_step_key: StepKey | null;
    is_complete: boolean;
  };
  banner: SetupBanner;
  integration: {
    email: string | null;
    email_pending: boolean;
    phone: string | null;
    phone_pending: boolean;
  };
  baa: {
    document_url: string;
    signed_at: string | null;
    signer_name: string;
    signer_email: string;
    sent_at: string | null;
    customer_signed_at: string | null;
    countersigner_name: string;
    countersigner_email: string;
    esign_ready: boolean;
    suggested_name: string;
    suggested_email: string;
  };
  group_admin: { submitted: boolean; invited_at: string | null };
  ehr:
    | { submitted: false }
    | {
        submitted: true;
        verification_status: "pending" | "verified" | "failed";
        verification_detail: string;
        submitted_at: string | null;
      };
  go_live: {
    providers: SetupProvider[];
    pulled_at: string | null;
    has_directory: boolean;
    member_management_url: string;
  };
  support: {
    contact: string;
    member_management_video_url: string;
    member_management_url: string;
  };
};

export type ProviderInviteState = "invited" | "joined" | null;

export type SetupProvider = {
  ehr_provider_id: string;
  name: string;
  specialty: string;
  credentials: string;
  username: string;
  email: string;
  invite_state: ProviderInviteState;
};

/** What an unverified visitor gets back. */
export type SetupGate = {
  group_name: string;
  requires_verification: true;
};

export type SetupResponse = SetupPortal | SetupGate;

export const isGated = (data: SetupResponse | undefined): data is SetupGate =>
  !!data && (data as SetupGate).requires_verification === true;

const readCookie = (name: string): string => {
  const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[2]) : "";
};

export const implementationApi = createApi({
  reducerPath: "implementationApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_URL,
    // The portal session lives in an HttpOnly cookie, so every call must carry
    // credentials — and every write must carry the CSRF token to match.
    credentials: "include",
    prepareHeaders: (headers, { type }) => {
      if (type === "mutation") {
        const csrf = readCookie("csrftoken");
        if (csrf) headers.set("X-CSRFToken", csrf);
      }
      return headers;
    },
  }),
  tagTypes: ["Portal"],
  endpoints: (builder) => ({
    getSetupPortal: builder.query<SetupResponse, string>({
      query: (token) => `/implementation/portal/${token}/`,
      providesTags: ["Portal"],
    }),

    requestSetupAccess: builder.mutation<{ detail: string }, { token: string; email: string }>({
      query: ({ token, email }) => ({
        url: `/implementation/portal/${token}/access/`,
        method: "POST",
        body: { email },
      }),
    }),

    verifySetupAccess: builder.mutation<SetupPortal, { token: string; accessToken: string }>({
      query: ({ token, accessToken }) => ({
        url: `/implementation/portal/${token}/access/verify/`,
        method: "POST",
        body: { access_token: accessToken },
      }),
      invalidatesTags: ["Portal"],
    }),

    submitGroupAdmin: builder.mutation<
      SetupPortal,
      { token: string; name: string; email: string; phone?: string; password: string }
    >({
      query: ({ token, ...body }) => ({
        url: `/implementation/portal/${token}/steps/group-admin/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Portal"],
    }),

    /**
     * Names who the BAA is issued to and sends it. DocuSign emails their signer
     * first, then Nudge to counter-sign. Re-callable to correct a mistyped address.
     */
    setBaaRecipient: builder.mutation<SetupPortal, { token: string; name: string; email: string }>({
      query: ({ token, ...body }) => ({
        url: `/implementation/portal/${token}/steps/baa/recipient/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Portal"],
    }),

    /** Re-delivers the DocuSign email — signing mail lands in spam often enough to need it. */
    resendBaa: builder.mutation<SetupPortal, { token: string }>({
      query: ({ token }) => ({
        url: `/implementation/portal/${token}/steps/baa/resend/`,
        method: "POST",
      }),
      invalidatesTags: ["Portal"],
    }),

    /** Asks DocuSign directly where the envelope stands — the customer's "I've signed it". */
    refreshBaaStatus: builder.mutation<SetupPortal, { token: string }>({
      query: ({ token }) => ({
        url: `/implementation/portal/${token}/steps/baa/refresh/`,
        method: "POST",
      }),
      invalidatesTags: ["Portal"],
    }),

    submitEhrLogin: builder.mutation<
      SetupPortal,
      {
        token: string;
        ehr_name: string;
        login_url?: string;
        username: string;
        password: string;
        auth_notes?: string;
        /** Up to 5 template names; blanks are dropped server-side. */
        priority_templates?: (string | undefined)[];
      }
    >({
      query: ({ token, ...body }) => ({
        url: `/implementation/portal/${token}/steps/ehr-login/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Portal"],
    }),

    inviteProvider: builder.mutation<
      SetupPortal & { invite_result?: { email: string; state: ProviderInviteState } },
      { token: string; email: string; name?: string }
    >({
      query: ({ token, ...body }) => ({
        url: `/implementation/portal/${token}/steps/go-live/invite/`,
        method: "POST",
        body,
      }),
      // No invalidatesTags on purpose: an invite doesn't change step status, and
      // a refetch here would unmount the open step mid-await (eating the row's
      // loading reset). The row reflects the result from local optimistic state;
      // a manual reload re-fetches the authoritative invite states.
    }),

    completeGoLive: builder.mutation<SetupPortal, { token: string }>({
      query: ({ token }) => ({
        url: `/implementation/portal/${token}/steps/go-live/complete/`,
        method: "POST",
      }),
      invalidatesTags: ["Portal"],
    }),
  }),
});

export const {
  useGetSetupPortalQuery,
  useRequestSetupAccessMutation,
  useVerifySetupAccessMutation,
  useSubmitGroupAdminMutation,
  useSubmitEhrLoginMutation,
  useSetBaaRecipientMutation,
  useResendBaaMutation,
  useRefreshBaaStatusMutation,
  useInviteProviderMutation,
  useCompleteGoLiveMutation,
} = implementationApi;

/** Used by the in-app banner for logged-in group admins. */
export type MyPortalSummary = {
  has_portal: boolean;
  url?: string;
  is_complete?: boolean;
  completed?: number;
  total?: number;
  current_step_key?: StepKey | null;
  action_needed?: boolean;
};
